import { Timestamp } from 'firebase/firestore'
import { firestore, storage } from '@utils/firebase'
import firebase from 'firebase/compat/app'
import { getUsersFromMembersPromise, getUsersPromise, updateMemberInfo } from './UserService'
import { collection, doc, updateDoc } from 'firebase/firestore'
import { Org, Member, OrgMembersData, InviteRequest } from '@Interfaces/Data/OrganizationInterface'
import { User } from '@Interfaces/Data/UserInterface'
import { ApiResponse } from '@Interfaces/Api/ResponsesInterface'
import { ApiError } from '@Interfaces/Api/ErrorsInterface'
import {
  UnsubscribeFunction,
  ListenerCallback,
  ErrorCallback,
} from '@Interfaces/Services/ServiceTypes'

export const getUserOrgsOnce = async (userId: string): Promise<ApiResponse<Org[]>> => {
  try {
    const querySnapshot = await firestore
      .collection('organizations')
      .where('members', 'array-contains', userId)
      .get()

    const organizations: Org[] = []
    querySnapshot.forEach((doc) => {
      const org: Org = {
        id: doc.id,
        coverPhoto: undefined,
        ...doc.data(),
      } as Org
      organizations.push(org)
    })

    const sorted = organizations.sort((a, b) =>
      (a?.lowercaseName || '') > (b?.lowercaseName || '') ? 1 : -1
    )

    return { success: true, data: sorted }
  } catch (error: any) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Error getting user organizations',
    }
  }
}

/**
 * Subscribe to user organizations
 * Returns unsubscribe function
 */
export const subscribeToUserOrgs = (
  userId: string,
  onUpdate: (orgs: Org[]) => void,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return firestore
    .collection('organizations')
    .where('members', 'array-contains', userId)
    .onSnapshot(
      (querySnapshot) => {
        const organizations: Org[] = []

        querySnapshot.forEach((doc) => {
          const org: Org = {
            id: doc.id,
            coverPhoto: undefined,
            ...doc.data(),
          } as Org
          organizations.push(org)
        })

        const sorted = organizations.sort((a, b) =>
          (a?.lowercaseName || '') > (b?.lowercaseName || '') ? 1 : -1
        )

        onUpdate(sorted)
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message || 'Error getting user organizations',
          details: error,
        })
      }
    )
}

/**
 * Get organization cover photo URL
 */
export const getOrgCoverPhoto = async (orgId: string): Promise<ApiResponse<string | null>> => {
  try {
    const picRef = storage.ref(`orgCoverPhotos/${orgId}/0.jpg`)
    const uri = await picRef.getDownloadURL()
    return {
      success: true,
      data: uri,
    }
  } catch (error: any) {
    return {
      success: true,
      data: null,
    }
  }
}

/**
 * Helper: Get org picture promise
 */
export const getOrgPicturePromise = (obj: any, orgId: string): Promise<any> => {
  return new Promise((resolve) => {
    const picRef = storage.ref(`orgCoverPhotos/${orgId}/0.jpg`)

    picRef
      .getDownloadURL()
      .then((uri) => {
        obj.coverPhoto = uri
        resolve(obj)
      })
      .catch(() => {
        resolve(obj)
      })
  })
}

/**
 * Get organization promise
 */
export const getOrgPromise = (obj: any, id: string): Promise<any> => {
  return new Promise((resolve) => {
    try {
      if (obj.cot) {
        obj.organization = obj.org || {}
        obj.organization.id = obj.orgId
        resolve(obj)
        return
      }

      firestore
        .collection('organizations')
        .doc(id)
        .get()
        .then((doc) => {
          if (doc.exists) {
            const org: Org = {
              id: doc.id,
              ...doc.data(),
            } as Org
            obj.organization = org
            resolve(obj)
          } else {
            resolve(null)
          }
        })
        .catch(() => {
          resolve(null)
        })
    } catch (err) {
      resolve(obj)
    }
  })
}

/**
 * Create a new organization
 */
const CREATED_ORG_TYPES = new Set<string>(['business', 'nonprofit', 'community'])

export const createOrganization = async (
  currentUser: User,
  organization: Partial<Org>,
  coverPhotos: string[],
  profilePics: string[]
): Promise<ApiResponse<string>> => {
  try {
    if (!organization.type || !CREATED_ORG_TYPES.has(organization.type)) {
      return {
        success: false,
        error: 'Organization type is required before creating an organization.',
        data: undefined,
      }
    }

    const orgRef = firestore.collection('organizations')

    if (organization.type === 'nonprofit') {
      organization.trendingScore = 10
    }
    if (organization.type === 'business') {
      organization.allowAnonymousDonations = false
      organization.allowDonationsToAllNonprofits = false
      organization.autoApproveDonations = false
      organization.fiscalYear = 'January'
      organization.hourlyVolunteerCompensation = 0
      organization.matchPercentage = 0
      organization.matchAnyDonation = true
      organization.maxCompanyMatchPerYear = 0
      organization.maxEmployeeMatchPerYear = 0
    }

    organization.createdAt = Timestamp.now()
    organization.members = [currentUser.id!]
    organization.lowercaseName = organization.name?.toLowerCase()
    organization.verified = false

    const docRef = await orgRef.add(organization)
    const membersRef = orgRef.doc(docRef.id).collection('members')

    await membersRef.doc(currentUser.id!).set({
      active: true,
      postNewOpportunities: true,
      publicPosts: true,
      role: 'Leader',
      verified: true,
      viewDonations: true,
      volunteerApplicationReview: true,
      volunteerCheckIn: true,
    })

    if (coverPhotos?.length > 0 || profilePics?.length > 0) {
      await updateOrgPics(docRef.id, organization, coverPhotos, profilePics)
    }

    return {
      success: true,
      data: docRef.id,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to create organization',
      data: undefined,
    }
  }
}

/**
 * Update organization
 */
export const updateOrganization = async (
  org: Org,
  data: Partial<Org>,
  coverPhotos: string[],
  profilePics: string[]
): Promise<ApiResponse<Org>> => {
  try {
    const orgRef = firestore.collection('organizations').doc(org.id!)

    if (coverPhotos?.length === 0 && profilePics?.length === 0) {
      await orgRef.update(data)
      return {
        success: true,
        data: { ...org, ...data } as Org,
      }
    } else {
      await updateOrgPics(org.id!, data, coverPhotos, profilePics)
      return {
        success: true,
        data: { ...org, ...data } as Org,
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'An error has occurred. Please try later.',
      data: undefined,
    }
  }
}

const PHOTO_CONFIG = {
  cover: { dir: 'orgCoverPhotos', field: 'coverPhoto' },
  profile: { dir: 'orgProfilePics', field: 'profilePic' },
  'theme.logo': { dir: 'logo', field: 'theme.logo' },
} as const

type PhotoType = keyof typeof PHOTO_CONFIG

/**
 * Helper: Update organization pictures
 */
const updateOrgPics = async (
  orgId: string,
  data: Partial<Org>,
  coverPhotos: string[],
  profilePics: string[]
): Promise<void> => {
  const orgRef = firestore.collection('organizations').doc(orgId)

  const photoTypes: { photos: string[]; type: 'cover' | 'profile' }[] = [
    { photos: coverPhotos, type: 'cover' },
    { photos: profilePics, type: 'profile' },
  ]

  const promises = photoTypes.flatMap(({ photos, type }) => {
    const { dir, field } = PHOTO_CONFIG[type]
    return photos.map((val, index) => {
      const ref = storage.ref(`${dir}/${orgId}/${index}.jpg`)
      return ref
        .putString(val, 'data_url')
        .then(() => ref.getDownloadURL())
        .then((downloadURL) => {
          data[field] = downloadURL
        })
        .catch(() => {
          // Silently fail
        })
    })
  })

  await Promise.all(promises)

  if (Object.keys(data).length > 0) {
    await orgRef.update(data)
  }
}

/**
 * Subscribe to organization members
 * Returns unsubscribe function
 */
export const subscribeToOrgMembers = (
  currentUser: User,
  organization: Org,
  onUpdate: (data: OrgMembersData) => void,
  onError?: ErrorCallback,
  onNotAllowed?: () => void
): UnsubscribeFunction => {
  return firestore
    .collection('organizations')
    .doc(organization.id!)
    .collection('members')
    .onSnapshot(
      async (membersQuerySnapshot) => {
        const membersData: Member[] = []

        membersQuerySnapshot.forEach((memberDoc) => {
          const memberData = memberDoc.data()
          const member: Member = {
            id: memberDoc.id,
            ...memberData,
          }
          membersData.push(member)
        })

        const promises = membersData.map((item) => getUsersFromMembersPromise(item.id, item))

        try {
          const users = await Promise.all(promises)
          const filteredDeleted = users.filter(
            (x): x is User & { role?: string; verified?: boolean } => x !== null
          )
          const filteredMembers = filteredDeleted.filter((x) => x.role !== 'Sponsor')
          const filteredSponsors = filteredDeleted.filter((x) => x.role === 'Sponsor')

          const findUs = filteredMembers.find((x) => x.id === currentUser.id)
          let userType: string | undefined
          let currentMember: (User & { role?: string; verified?: boolean }) | undefined

          if (findUs) {
            if (findUs.role !== 'Leader' && organization.type === 'business') {
              onNotAllowed?.()
              onError?.({
                code: 'organizations/not-leader',
                message:
                  'You are not a leader of this organization. Please contact your organization admin for more information.',
                details: null,
              })
              return
            }

            currentMember = findUs
            userType = findUs.role

            if (!findUs.verified) {
              await updateMemberInfo(organization, currentUser, { verified: true })
            }
          }

          onUpdate({
            members: filteredMembers,
            sponsors: filteredSponsors,
            userType,
            currentMember,
          })
        } catch (error: any) {
          onError?.({
            code: 'firestore/error',
            message: error.message || 'Error getting organization members',
            details: error,
          })
        }
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message || 'Error getting organization members',
          details: error,
        })
      }
    )
}

/**
 * Subscribe to invited members
 * Returns unsubscribe function
 */
export const subscribeToInvitedMembers = (
  organization: Org,
  onUpdate: (invites: InviteRequest[]) => void,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return firestore
    .collection('inviteRequests')
    .where('orgId', '==', organization.id)
    .onSnapshot(
      (querySnapshot) => {
        const temp: InviteRequest[] = []
        querySnapshot.forEach((memberDoc) => {
          const data = memberDoc.data()
          const invite: InviteRequest = {
            id: memberDoc.id,
            orgId: organization.id!,
            invite: true,
            ...data,
          }
          temp.push(invite)
        })
        onUpdate(temp)
      },
      (error) => {
        onError?.({
          code: 'firestore/error',
          message: error.message || 'Error getting invited members',
          details: error,
        })
      }
    )
}

/**
 * Get organization members (wrapper for subscribeToOrgMembers)
 * Returns unsubscribe function
 */
export const getOrgMembers = (
  currentUser: User,
  organization: Org,
  onUpdate: (data: OrgMembersData) => void,
  onError?: ErrorCallback,
  onNotAllowed?: () => void
): UnsubscribeFunction => {
  return subscribeToOrgMembers(currentUser, organization, onUpdate, onError, onNotAllowed)
}

/**
 * Get organization invited members (wrapper for subscribeToInvitedMembers)
 * Returns unsubscribe function
 */
export const getOrgInvitedMembers = (
  organization: Org,
  onUpdate: (invites: InviteRequest[]) => void,
  onError?: ErrorCallback
): UnsubscribeFunction => {
  return subscribeToInvitedMembers(organization, onUpdate, onError)
}

/**
 * Add new user to organization
 */
export const organizationAddNewUser = async (
  organization: Org,
  userId: string,
  role: string,
  isVerified: boolean
): Promise<ApiResponse<boolean>> => {
  try {
    await firestore
      .collection('organizations')
      .doc(organization.id!)
      .collection('members')
      .doc(userId)
      .set({
        role: role,
        verified: isVerified || false,
        publicPosts: false,
        postNewOpportunities: false,
        volunteerApplicationReview: false,
        volunteerCheckIn: false,
        viewDonations: false,
      })

    await firestore
      .collection('organizations')
      .doc(organization.id!)
      .update({
        members: firebase.firestore.FieldValue.arrayUnion(userId),
      })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to add user to organization',
      data: undefined,
    }
  }
}

/**
 * Invite a member
 */
export const inviteMember = async (data: any): Promise<ApiResponse<string>> => {
  try {
    const docRef = await firestore.collection('inviteRequests').add(data)
    return {
      success: true,
      data: docRef.id,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to invite member',
      data: undefined,
    }
  }
}

/**
 * Archive or activate a user
 */
export const archiveUser = async (
  orgId: string,
  userId: string,
  active: boolean,
  invite: boolean
): Promise<ApiResponse<boolean>> => {
  try {
    if (invite) {
      await firestore.collection('inviteRequests').doc(userId).delete()
      return {
        success: true,
        data: true,
      }
    }

    await firestore
      .collection('organizations')
      .doc(orgId)
      .update({
        members: active
          ? firebase.firestore.FieldValue.arrayUnion(userId)
          : firebase.firestore.FieldValue.arrayRemove(userId),
      })

    await firestore
      .collection('organizations')
      .doc(orgId)
      .collection('members')
      .doc(userId)
      .update({
        active,
      })

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to archive user',
      data: undefined,
    }
  }
}

/**
 * Get organization followers
 */
export const getOrgFollowers = async (organization: Org): Promise<ApiResponse<User[]>> => {
  try {
    const querySnapshot = await firestore
      .collection('org_following')
      .where('orgId', '==', organization.id)
      .get()

    const followers: string[] = []
    querySnapshot.forEach((doc) => {
      const subDocData = doc.data()
      followers.push(subDocData.userId)
    })

    const promises = followers.map((item) => getUsersPromise(item))
    const users = await Promise.all(promises)
    const orgFollowers = users.filter((x): x is User => x !== null)

    return {
      success: true,
      data: orgFollowers,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get organization followers',
      data: undefined,
    }
  }
}

/**
 * Check if custom URL is available
 */
export const checkCustomUrls = async (customUrl: string): Promise<ApiResponse<boolean>> => {
  try {
    const usernameQuery = await firestore
      .collection('usernames')
      .where('username', '==', customUrl)
      .get()

    if (usernameQuery.size > 0) {
      return {
        success: true,
        data: true,
      }
    }

    const customUrlQuery = await firestore.collection('custom_urls').doc(customUrl).get()

    return {
      success: true,
      data: customUrlQuery.exists,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to check custom URL',
      data: undefined,
    }
  }
}

/**
 * Save custom organization URL
 */
export const saveCustomOrgUrl = async (
  selectedOrg: Org,
  customUrl: string
): Promise<ApiResponse<boolean>> => {
  try {
    await firestore.collection('custom_urls').doc(customUrl).set({ orgId: selectedOrg.id })

    await firestore.collection('organizations').doc(selectedOrg.id!).update({ customUrl })

    const querySnapshot = await firestore
      .collection('custom_urls')
      .where('orgId', '==', selectedOrg.id)
      .get()

    const deletePromises = querySnapshot.docs
      .filter((doc) => doc.id !== customUrl)
      .map((doc) => doc.ref.delete())

    await Promise.all(deletePromises)

    return {
      success: true,
      data: true,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to save custom URL',
      data: undefined,
    }
  }
}

/**
 * Upload organization photo
 */
export const uploadPhoto = async ({
  orgId,
  imageUrl,
  index = 0,
  type,
}: {
  orgId: string
  imageUrl: string
  index?: number
  type: PhotoType
}): Promise<
  ApiResponse<{ orgId: string; coverPhoto?: string; profilePic?: string; 'theme.logo'?: string }>
> => {
  try {
    const { dir, field } = PHOTO_CONFIG[type]

    const imageStorageRef = storage.ref(`${dir}/${orgId}/${index}.jpg`)
    await imageStorageRef.putString(imageUrl, 'data_url')
    const imageStorageUrl = await imageStorageRef.getDownloadURL()

    const orgRef = doc(firestore, 'organizations', orgId)
    await updateDoc(orgRef, { [field]: imageStorageUrl })

    const payload: {
      orgId: string
      coverPhoto?: string
      profilePic?: string
      'theme.logo'?: string
    } = {
      orgId,
      [field]: imageStorageUrl,
    }
    return {
      success: true,
      data: payload,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to upload photo',
      data: undefined,
    }
  }
}

/**
 * Get organization by ID
 */
export const getOrgById = async (orgId: string): Promise<ApiResponse<Org | null>> => {
  if (!orgId) {
    return {
      success: true,
      data: null,
    }
  }

  try {
    const orgDoc = await firestore.collection('organizations').doc(orgId).get()

    if (orgDoc.exists) {
      const data = orgDoc.data()
      if (!data) {
        return {
          success: true,
          data: null,
        }
      }
      return {
        success: true,
        data: {
          id: orgDoc.id,
          ...data,
        } as Org,
      }
    }

    return {
      success: true,
      data: null,
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to get organization',
      data: undefined,
    }
  }
}
